import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter } from "react-router-dom";
import RouteGuard from "../guards/RouteGuard";
import PageFallback from "./PageFallback";

const DashboardLayout = lazy(() => import("../layouts/DashboardLayout"));
const AffiliateDashboard = lazy(() => import("../pages/affiliate/dashboard/AffiliateDashboard"));
const AffiliateSearchCompanies = lazy(
  () => import("../pages/affiliate/searchCompanies/AffiliateSearchCompanies"),
);
const AffiliateTransactions = lazy(
  () => import("../pages/affiliate/transactions/AffiliateTransactions"),
);
const AffiliateProfileSettings = lazy(
  () => import("../pages/affiliate/settings/AffiliateProfileSettings"),
);
const StripeConnectSuccess = lazy(() => import("../components/StripeConnectSuccess"));
const SignIn = lazy(() => import("../pages/auth/SignIn"));
const SplitLayout = lazy(() => import("../components/SplitLayout"));
const SignUp = lazy(() => import("../pages/auth/SignUp"));
const ForgotPassword = lazy(() => import("../pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("../pages/auth/ResetPassword"));
const VerifyOtp = lazy(() => import("../pages/auth/VerifyOtp"));
const Referrals = lazy(() => import("../pages/affiliate/affiliates/Referrals"));
const AuthSuccess = lazy(() => import("../pages/auth/AuthSuccess"));
const PageNotFound = lazy(() => import("../pages/PageNotFound"));

function SuspensePage({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>;
}

const routes = createBrowserRouter([
  {
    path: "/",
    element: <RouteGuard />,
    children: [
      {
        path: "/",
        element: (
          <SuspensePage>
            <DashboardLayout />
          </SuspensePage>
        ),
        children: [
          {
            path: "dashboard",
            element: (
              <SuspensePage>
                <AffiliateDashboard />
              </SuspensePage>
            ),
          },
          {
            path: "search-companies",
            element: (
              <SuspensePage>
                <AffiliateSearchCompanies />
              </SuspensePage>
            ),
          },
          {
            path: "transactions",
            element: (
              <SuspensePage>
                <AffiliateTransactions />
              </SuspensePage>
            ),
          },
          {
            path: "referrals",
            element: (
              <SuspensePage>
                <Referrals />
              </SuspensePage>
            ),
          },
          {
            path: "settings",
            element: (
              <SuspensePage>
                <AffiliateProfileSettings />
              </SuspensePage>
            ),
          },
        ],
      },

      {
        path: "/stripe/connect/success",
        element: (
          <SuspensePage>
            <StripeConnectSuccess />
          </SuspensePage>
        ),
      },
    ],
  },

  {
    path: "/auth",
    element: (
      <SuspensePage>
        <SplitLayout />
      </SuspensePage>
    ),
    children: [
      {
        path: "sign-in",
        element: (
          <SuspensePage>
            <SignIn />
          </SuspensePage>
        ),
      },
      {
        path: "sign-up",
        element: (
          <SuspensePage>
            <SignUp />
          </SuspensePage>
        ),
      },
      {
        path: "forgot-password",
        element: (
          <SuspensePage>
            <ForgotPassword />
          </SuspensePage>
        ),
      },
      {
        path: "verify-otp",
        element: (
          <SuspensePage>
            <VerifyOtp />
          </SuspensePage>
        ),
      },
      {
        path: "reset-password",
        element: (
          <SuspensePage>
            <ResetPassword />
          </SuspensePage>
        ),
      },
      {
        path: "success",
        element: (
          <SuspensePage>
            <AuthSuccess />
          </SuspensePage>
        ),
      },
    ],
  },

  {
    path: "*",
    element: (
      <SuspensePage>
        <PageNotFound />
      </SuspensePage>
    ),
  },
]);

export default routes;
